import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI, uploadAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, Upload, Loader2, Package, FileText, Download, ShoppingCart, Layers } from 'lucide-react';
import PlanLimitBanner from '../components/PlanLimitBanner';

const PRODUCT_TYPES = ['Ebook', 'Course', 'Template', 'Preset', 'Poster'];

const TYPE_COLORS = {
  Ebook: 'bg-blue-100 text-blue-700',
  Course: 'bg-green-100 text-green-700',
  Template: 'bg-amber-100 text-amber-700',
  Preset: 'bg-purple-100 text-purple-700',
  Poster: 'bg-rose-100 text-rose-700',
  Bundle: 'bg-teal-100 text-teal-700',
};

const emptyProduct = {
  name: '', description: '', price: '', type: 'Ebook', max_downloads: 5, expiry_hours: 72,
  file_id: '', file_name: '', thumbnail_url: '', is_published: false
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyProduct });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  // Bundle state
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const [bundleForm, setBundleForm] = useState({ name: '', description: '', price: '', product_ids: [], thumbnail_url: '', max_downloads: 5, expiry_hours: 72 });
  const [savingBundle, setSavingBundle] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsAPI.list();
      setProducts(res.data);
      
      // Auto-refresh bundle thumbnails that aren't manually set
      const bundlesToRefresh = res.data.filter(p => p.is_bundle && !p.thumbnail_manually_set);
      if (bundlesToRefresh.length > 0) {
        const refreshPromises = bundlesToRefresh.map(b =>
          productsAPI.refreshBundleThumbnail(b.id).catch(() => null)
        );
        const refreshed = await Promise.all(refreshPromises);
        
        // Update products with refreshed thumbnail data
        const refreshedMap = {};
        refreshed.forEach(r => {
          if (r?.data?.id) refreshedMap[r.data.id] = r.data;
        });
        
        if (Object.keys(refreshedMap).length > 0) {
          setProducts(prev => prev.map(p => refreshedMap[p.id] || p));
        }
      }
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyProduct });
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      type: product.type || 'Ebook',
      max_downloads: product.max_downloads || 5,
      expiry_hours: product.expiry_hours || 72,
      file_id: product.file_id || '',
      file_name: product.file_name || '',
      thumbnail_url: product.thumbnail_url || '',
      is_published: product.is_published || false,
    });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.file(fd);
      setForm(prev => ({ ...prev, file_id: res.data.file_id, file_name: res.data.filename }));
      toast.success(`File uploaded: ${res.data.filename}`);
    } catch (err) {
      toast.error('File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.thumbnail(fd);
      setForm(prev => ({ ...prev, thumbnail_url: res.data.thumbnail_url }));
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error('Thumbnail upload failed');
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Product name is required'); return; }
    if (!form.price || parseFloat(form.price) < 0) { toast.error('Valid price is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) {
        await productsAPI.update(editing.id, payload);
        toast.success('Product updated');
      } else {
        await productsAPI.create(payload);
        toast.success('Product created');
      }
      setDialogOpen(false);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted');
      loadProducts();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Bundle handlers
  const nonBundleProducts = products.filter(p => !p.is_bundle);
  const bundles = products.filter(p => p.is_bundle);

  const openBundleCreate = () => {
    setEditingBundle(null);
    setBundleForm({ name: '', description: '', price: '', product_ids: [], thumbnail_url: '', max_downloads: 5, expiry_hours: 72 });
    setBundleDialogOpen(true);
  };

  const openBundleEdit = (bundle) => {
    setEditingBundle(bundle);
    setBundleForm({
      name: bundle.name || '',
      description: bundle.description || '',
      price: bundle.price?.toString() || '',
      product_ids: bundle.bundle_product_ids || [],
      thumbnail_url: bundle.thumbnail_url || '',
      thumbnail_manually_set: bundle.thumbnail_manually_set || false,
      max_downloads: bundle.max_downloads || 5,
      expiry_hours: bundle.expiry_hours || 72,
    });
    setBundleDialogOpen(true);
  };

  const toggleBundleProduct = (pid) => {
    setBundleForm(prev => {
      const ids = prev.product_ids || [];
      return { ...prev, product_ids: ids.includes(pid) ? ids.filter(i => i !== pid) : [...ids, pid] };
    });
  };

  const bundleIndividualTotal = bundleForm.product_ids.reduce((sum, pid) => {
    const p = nonBundleProducts.find(pr => pr.id === pid);
    return sum + (p?.price || 0);
  }, 0);

  const handleBundleSave = async () => {
    if (!bundleForm.name.trim()) { toast.error('Bundle name required'); return; }
    if (bundleForm.product_ids.length < 2) { toast.error('Select at least 2 products'); return; }
    if (!bundleForm.price || parseFloat(bundleForm.price) < 0) { toast.error('Valid price required'); return; }
    setSavingBundle(true);
    try {
      const payload = {
        name: bundleForm.name,
        description: bundleForm.description,
        price: parseFloat(bundleForm.price),
        product_ids: bundleForm.product_ids,
        bundle_product_ids: bundleForm.product_ids,
        max_downloads: bundleForm.max_downloads,
        expiry_hours: bundleForm.expiry_hours,
        is_published: true,
      };
      // Only send thumbnail info if manually uploaded
      if (bundleForm.thumbnail_manually_set && bundleForm.thumbnail_url) {
        payload.thumbnail_url = bundleForm.thumbnail_url;
        payload.thumbnail_manually_set = true;
      } else {
        payload.thumbnail_manually_set = false;
      }
      
      if (editingBundle) {
        await productsAPI.updateBundle(editingBundle.id, payload);
        toast.success('Bundle updated');
      } else {
        await productsAPI.createBundle(payload);
        toast.success('Bundle created');
      }
      setBundleDialogOpen(false);
      loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bundle save failed');
    } finally {
      setSavingBundle(false);
    }
  };

  const handleBundleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.thumbnail(fd);
      setBundleForm(prev => ({ ...prev, thumbnail_url: res.data.thumbnail_url, thumbnail_manually_set: true }));
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingThumb(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-64 rounded-xl bg-muted animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground mt-1">{products.length} product{products.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openBundleCreate} data-testid="products-create-bundle-button">
            <Layers className="h-4 w-4 mr-1.5" /> Create Bundle
          </Button>
          <Button onClick={() => navigate('/app/products/new')} data-testid="products-create-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add Product
          </Button>
        </div>
      </div>

      <PlanLimitBanner resource="products" label="products" />

      {products.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold">No products yet</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create your first digital product to start selling</p>
            <Button onClick={() => navigate('/app/products/new')}><Plus className="h-4 w-4 mr-1.5" /> Create Product</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-table">
          {products.map(product => (
            <Card key={product.id} className="card-hover overflow-hidden">
              {product.thumbnail_url ? (
                <div className="aspect-video bg-muted">
                  <img src={product.thumbnail_url} alt={product.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="aspect-video bg-muted flex items-center justify-center">
                  <FileText className="h-10 w-10 text-muted-foreground/30" />
                </div>
              )}
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{product.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className={TYPE_COLORS[product.type] || ''}>{product.type}</Badge>
                      {product.is_published ? (
                        <Badge variant="default" className="bg-emerald-100 text-emerald-700">Published</Badge>
                      ) : (
                        <Badge variant="secondary">Draft</Badge>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => product.is_bundle ? openBundleEdit(product) : openEdit(product)}>
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(product.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-xl font-semibold mt-3">RM {parseFloat(product.price || 0).toFixed(2)}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Download className="h-3 w-3" /> {product.max_downloads} downloads</span>
                  <span>{product.expiry_hours}h expiry</span>
                </div>
                {product.file_name && (
                  <p className="text-xs text-muted-foreground mt-1 truncate font-mono">File: {product.file_name}</p>
                )}
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><ShoppingCart className="h-3 w-3" /> {product.total_sales || 0} sales &middot; RM {(product.total_revenue || 0).toFixed(2)} revenue</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Product' : 'Create Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Product Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Instagram Templates Pack" data-testid="product-form-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe your product..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (MYR)</Label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="29.90" data-testid="product-form-price-input" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger data-testid="product-form-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Validity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Downloads</Label>
                <Input type="number" min="1" value={form.max_downloads} onChange={e => setForm(p => ({ ...p, max_downloads: parseInt(e.target.value) || 1 }))} data-testid="product-form-download-limit-input" />
              </div>
              <div className="space-y-2">
                <Label>Link Expiry (hours)</Label>
                <Input type="number" min="1" value={form.expiry_hours} onChange={e => setForm(p => ({ ...p, expiry_hours: parseInt(e.target.value) || 1 }))} data-testid="product-form-expiry-input" />
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Digital Product File</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                {form.file_name ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="text-sm font-mono">{form.file_name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, file_id: '', file_name: '' }))}>Change</Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="product-form-file-upload" />
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-muted-foreground/50" />
                        <span className="text-sm text-muted-foreground">Click to upload file</span>
                        <span className="text-xs text-muted-foreground">PDF, ZIP, MP4, etc. Max 100MB</span>
                      </div>
                    )}
                  </label>
                )}
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="space-y-2">
              <Label>Thumbnail Image</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                {form.thumbnail_url ? (
                  <div className="space-y-2">
                    <img src={form.thumbnail_url} alt="Thumb" className="w-24 h-24 object-cover mx-auto rounded-lg" />
                    <Button variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, thumbnail_url: '' }))}>Remove</Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} disabled={uploadingThumb} />
                    {uploadingThumb ? (
                      <div className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Click to upload thumbnail (max 5MB)</span>
                    )}
                  </label>
                )}
              </div>
            </div>

            {/* Published toggle */}
            <div className="flex items-center justify-between">
              <Label>Published</Label>
              <Switch checked={form.is_published} onCheckedChange={v => setForm(p => ({ ...p, is_published: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="product-form-save-button">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle Create/Edit Dialog */}
      <Dialog open={bundleDialogOpen} onOpenChange={setBundleDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBundle ? 'Edit Bundle' : 'Create Bundle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Bundle Name</Label>
              <Input value={bundleForm.name} onChange={e => setBundleForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Ultimate Creator Pack" data-testid="bundle-form-name" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={bundleForm.description} onChange={e => setBundleForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe your bundle..." rows={2} />
            </div>

            {/* Product Selection */}
            <div className="space-y-2">
              <Label>Select Products ({bundleForm.product_ids.length} selected)</Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-lg p-2">
                {nonBundleProducts.length > 0 ? nonBundleProducts.map(p => (
                  <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors duration-150 ${bundleForm.product_ids.includes(p.id) ? 'bg-accent border border-primary/30' : 'hover:bg-muted/60 border border-transparent'}`}>
                    <input
                      type="checkbox"
                      checked={bundleForm.product_ids.includes(p.id)}
                      onChange={() => toggleBundleProduct(p.id)}
                      className="accent-[hsl(var(--primary))] h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.type}</p>
                    </div>
                    <span className="text-sm font-semibold shrink-0">RM {parseFloat(p.price || 0).toFixed(2)}</span>
                  </label>
                )) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No products available. Create products first.</p>
                )}
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-2">
              <Label>Bundle Price (MYR)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number" min="0" step="0.01"
                  value={bundleForm.price}
                  onChange={e => setBundleForm(p => ({ ...p, price: e.target.value }))}
                  placeholder="49.90"
                  className="flex-1"
                  data-testid="bundle-form-price"
                />
              </div>
              {bundleForm.product_ids.length >= 2 && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Individual total: <span className="line-through">RM {bundleIndividualTotal.toFixed(2)}</span></span>
                  {bundleForm.price && parseFloat(bundleForm.price) < bundleIndividualTotal && (
                    <Badge variant="default" className="bg-emerald-100 text-emerald-700 text-[10px]">
                      Save {Math.round((1 - parseFloat(bundleForm.price) / bundleIndividualTotal) * 100)}%
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Thumbnail */}
            <div className="space-y-2">
              <Label>Thumbnail</Label>
              <p className="text-xs text-muted-foreground">Auto-generated from selected products. Upload to override.</p>
              {bundleForm.thumbnail_url ? (
                <div className="relative w-32">
                  <img src={bundleForm.thumbnail_url} alt="Bundle" className="w-32 h-32 rounded-lg object-cover border" />
                  <button onClick={() => setBundleForm(p => ({ ...p, thumbnail_url: '', thumbnail_manually_set: false }))} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center text-xs">&times;</button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleBundleThumbUpload} disabled={uploadingThumb} />
                  <span className="text-sm text-primary hover:underline cursor-pointer">Upload custom thumbnail</span>
                </label>
              )}
            </div>

            {/* Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Downloads</Label>
                <Input type="number" min="1" value={bundleForm.max_downloads} onChange={e => setBundleForm(p => ({ ...p, max_downloads: parseInt(e.target.value) || 1 }))} />
              </div>
              <div className="space-y-2">
                <Label>Expiry (hours)</Label>
                <Input type="number" min="1" value={bundleForm.expiry_hours} onChange={e => setBundleForm(p => ({ ...p, expiry_hours: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBundleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBundleSave} disabled={savingBundle} data-testid="bundle-form-save">
              {savingBundle ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Layers className="h-4 w-4 mr-1.5" />}
              {editingBundle ? 'Update Bundle' : 'Create Bundle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
